import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { KeyedMutator, MutatorCallback } from 'swr'
import { likePost, unlikePost } from 'modules/community/api/community-api'
import { PostInterface } from 'modules/community/types/post-interface'
import { getPostActions } from '../post/utils/post-utils'

interface UseToggleLikeProps<T extends PostInterface> {
  mutatePosts: KeyedMutator<T[]>
  communityPath: string
}

export const useToggleLike = <T extends PostInterface>({
  mutatePosts,
  communityPath,
}: UseToggleLikeProps<T>) => {
  const { t } = useTranslation()

  const handleUnlike = async (
    id: number,
    globalMutatePost: (mutator: MutatorCallback<T>) => Promise<T | undefined>,
  ) => {
    await unlikePost(id)
    await mutatePosts(
      data =>
        data &&
        data.map(post =>
          post.id === id ? { ...post, isLiked: false, likesCount: post.likesCount - 1 } : post,
        ),
      false,
    )
    globalMutatePost(post => post && { ...post, isLiked: false, likesCount: post.likesCount - 1 })
  }

  const handleLike = async (
    id: number,
    globalMutatePost: (mutator: MutatorCallback<T>) => Promise<T | undefined>,
  ) => {
    await likePost(id)
    await mutatePosts(
      data =>
        data &&
        data.map(post =>
          post.id === id ? { ...post, isLiked: true, likesCount: post.likesCount + 1 } : post,
        ),
      false,
    )
    globalMutatePost(post => post && { ...post, isLiked: true, likesCount: post.likesCount + 1 })
  }

  return useCallback(
    ({ id, topic, path, isLiked }: T) =>
      async () => {
        const { globalMutatePost } = getPostActions({
          communityPath: communityPath,
          topicPath: topic.path,
          postPath: path,
        }) as { globalMutatePost: (mutator: MutatorCallback<T>) => Promise<T | undefined> }

        try {
          if (isLiked) {
            handleUnlike(id, globalMutatePost)
          } else {
            handleLike(id, globalMutatePost)
          }
        } catch (err) {
          toast.error(t('post_details.error.error_like_post'))
        }
      },
    [mutatePosts, t, communityPath],
  )
}
